import { Modal, Descriptions, Tag, Button, Typography, message, Alert } from "antd"
import { CopyOutlined, MailOutlined } from "@ant-design/icons"
import type React from "react"
import type { EmailLogItem } from "@/services/log/emailLogApi"
import dayjs from "dayjs"

const { Text } = Typography

interface EmailLogDetailModalProps {
  open: boolean
  record: EmailLogItem | null
  onClose: () => void
}

export const EmailLogDetailModal: React.FC<EmailLogDetailModalProps> = ({
  open,
  record,
  onClose,
}) => {
  if (!record) return null

  const isSuccess = record.status === "sent"
  let formattedMetadata = ""
  let hasMetadata = false

  if (record.metadata) {
    try {
      formattedMetadata =
        typeof record.metadata === "string"
          ? JSON.stringify(JSON.parse(record.metadata), null, 2)
          : JSON.stringify(record.metadata, null, 2)
      hasMetadata = true
    } catch {
      formattedMetadata = String(record.metadata)
      hasMetadata = true
    }
  }

  const handleCopyMetadata = () => {
    if (formattedMetadata) {
      navigator.clipboard.writeText(formattedMetadata)
      message.success("Đã sao chép Metadata vào clipboard!")
    }
  }

  return (
    <Modal
      title={
        <div className="flex items-center gap-2 pr-6">
          <MailOutlined className="text-blue-600 text-lg" />
          <span>Chi tiết Email Log #{record.id}</span>
          <Tag color={isSuccess ? "success" : "error"}>
            {isSuccess ? "Thành công" : "Thất bại"}
          </Tag>
          <Tag color="geekblue">{record.mailer || "smtp"}</Tag>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        hasMetadata && (
          <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyMetadata}>
            Sao chép Metadata
          </Button>
        ),
        <Button key="close" type="primary" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <div className="flex flex-col gap-4 py-2">
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Người nhận (To)" span={2}>
            <span className="font-semibold text-blue-600">{record.to_address}</span>
          </Descriptions.Item>

          <Descriptions.Item label="Tiêu đề (Subject)" span={2}>
            <span className="font-medium text-gray-900">{record.subject}</span>
          </Descriptions.Item>

          <Descriptions.Item label="Người gửi (From)">
            {record.from_address ? (
              <span className="text-gray-700">{record.from_address}</span>
            ) : (
              <Text type="secondary">Mặc định hệ thống</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Mailer">
            <Tag color="geekblue" className="font-mono">
              {record.mailer || "smtp"}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Message ID" span={2}>
            {record.message_id ? (
              <span className="font-mono text-xs text-gray-700 break-all">{record.message_id}</span>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Thời gian gửi">
            {record.created_at ? dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss") : "—"}
          </Descriptions.Item>

          <Descriptions.Item label="Trạng thái">
            <Tag color={isSuccess ? "success" : "error"}>
              {isSuccess ? "Thành công (sent)" : "Thất bại (failed)"}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {record.error_message && (
          <div>
            <div className="mb-1.5 font-semibold text-rose-600">Thông báo lỗi (Error Message):</div>
            <Alert
              type="error"
              showIcon
              message={
                <div className="max-h-[200px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap break-all text-xs font-mono m-0">
                    {record.error_message}
                  </pre>
                </div>
              }
            />
          </div>
        )}

        {hasMetadata && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-semibold text-gray-800">
                Metadata <Tag color="cyan">JSON Format</Tag>
              </span>
            </div>
            <div className="max-h-[300px] overflow-y-auto rounded-md border border-gray-200 bg-gray-900 p-3 text-xs text-gray-100 font-mono shadow-inner">
              <pre className="whitespace-pre-wrap break-all leading-relaxed m-0 font-mono">
                {formattedMetadata}
              </pre>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default EmailLogDetailModal
